import { useState } from "react";
import { Box, Button, Grid, MenuItem, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { addDays, format } from "date-fns";
import { pt } from "date-fns/locale";
import { getItems } from "../../../api/items";
import {
    computeKpiCells,
    countWhere,
    monthlySeriesFromRows,
    treatmentsWithFirstSessionInRange,
} from "../../../dashboard-framework/aggregateRows";
import { DASHBOARD_COLORS } from "../../../dashboard-framework/colors";
import { cellMatchToFilter, drillDownForKpiCell, drillDownFromAggregate } from "../../../dashboard-framework/drilldown";
import { describeKpiCell, describeSessionsCount, formatDateRangePt, SESSION_STATUS_LABELS_PT } from "../../../dashboard-framework/describeWidgetLogic";
import { combineFilters } from "../../../dashboard-framework/useAggregate";
import { useRecords } from "../../../api/useRecords";
import { buildWidgetFilter } from "../../../dashboard-framework/buildWidgetFilter";
import { comparisonWeekPeriods, resolveRequiredDateFilterRange } from "../../../dashboard-framework/datePresets";
import { useDashboardAccess } from "../../../dashboard-framework/DashboardAccessProvider";
import { DashboardPage } from "../../../dashboard-framework/DashboardPage";
import { useAggregate } from "../../../dashboard-framework/useAggregate";
import { useGlobalFilterValues } from "../../../dashboard-framework/useGlobalFilterValues";
import { ComparisonCard } from "../../../dashboard-framework/widgets/ComparisonCard";
import { GaugeCard } from "../../../dashboard-framework/widgets/GaugeCard";
import { StatCard } from "../../../dashboard-framework/widgets/StatCard";
import { TimeSeriesChart } from "../../../dashboard-framework/widgets/TimeSeriesChart";
import { WidgetCard } from "../../../dashboard-framework/widgets/WidgetCard";
import { NFB_SERVICE, PSY_SERVICE } from "../../../utils/constants";
import { formatDrillDateTime, SESSIONS_DRILL_FIELDS, sessionRoute, sessionsDrillColumns } from "../shared/drillColumns";
import {
    DONE_AND_TO_COME_STATUSES,
    monthlySessionsQuery,
    NFB_PSY_SERVICES,
    sessionsChartSeries,
    TREATMENT_FIRST_SESSION_STARTED_OR_TO_COME_STATUSES,
    treatmentServicesLabelByTreatmentId,
    treatmentsFirstStartedSessionQuery,
    treatmentsServiceMixQuery,
} from "../shared/sharedMetrics";
import {
    CLIENT_TYPE_OPTIONS,
    clientTypeFilterByOption,
    CLINIC_ROW_COLORS,
    DAILY_ANALYSIS_CLINIC_OPTIONS,
    dailyAnalysisGaugeGoals,
    dailyAnalysisGlobalFilters,
    dailyAnalysisKpiCells,
    dailyAnalysisKpiRows,
    evaluationsDoneGaugeMatch,
    EXCLUDE_VIRTUAL_CLINIC_FILTER,
    sessionsByStatusAndServiceQuery,
    totalSessionsDoneGaugeMatch,
    treatmentsFirstStartedOrToComeSessionQuery,
    weeklySessionsByServiceQuery,
} from "./dailyAnalysisConfig";
import {
    FIRST_SESSION_ENRICHMENT_FIELDS,
    firstSessionByTreatmentIdFromItems,
    treatmentClinicChips,
    treatmentFirstSessionDrillRows,
    treatmentFirstSessionHeaderChips,
    treatmentServiceMixChips,
} from "./dailyAnalysisRows";

const ALL_OPTION_VALUE = "__all__";

// Clínicas mostradas como totais no cabeçalho das tabelas de tratamentos.
const CLINIC_CHIP_NAMES = ["Porto", "Braga"];

// Colunas, cor de linha por clínica e configuração base partilhadas pelas tabelas "Tratamentos Iniciados"
// e "Tratamentos Iniciados + Por Vir" (a única diferença entre elas é o filtro/linhas e o título).
const TREATMENT_FIRST_SESSION_COLUMNS = [
    { key: "clientName", label: "Cliente" },
    { key: "firstSessionDate", label: "1ª sessão NFB/Psi", valueFor: (row: any) => formatDrillDateTime(row.firstSessionDate) },
    { key: "clinicName", label: "Clínica" },
    { key: "serviceName", label: "Serviço" },
    { key: "servicesLabel", label: "Tratamento" },
    { key: "statusLabel", label: "Estado" },
];

const treatmentRowClinicSx = (row: any) => {
    const clinicColor = row.clinicName ? CLINIC_ROW_COLORS[row.clinicName] : undefined;
    const sx = clinicColor ? { backgroundColor: `${clinicColor}1A` } : {};
    return sx;
};

const useFirstSessionsEnrichment = (queryKey: Array<unknown>, treatmentIds: Array<number | string>, statuses: Array<string>, enabled: boolean) => {
    const result = useQuery({
        queryKey,
        queryFn: () =>
            getItems("sessions", FIRST_SESSION_ENRICHMENT_FIELDS, {
                filter: {
                    _and: [{ treatment: { _in: treatmentIds } }, { service: { _in: NFB_PSY_SERVICES } }, { status: { _in: statuses } }, EXCLUDE_VIRTUAL_CLINIC_FILTER],
                },
                pageSize: 1000,
                sort: "start_date",
            }),
        enabled,
        staleTime: 5 * 60 * 1000,
    });
    return result;
};

export const DailyAnalysisDashboard = () => {
    const access = useDashboardAccess();
    const { canDrill } = access;
    const { values, setValue } = useGlobalFilterValues(dailyAnalysisGlobalFilters);
    const [chartClinic, setChartClinic] = useState<number | null>(null);
    const [chartClientType, setChartClientType] = useState<string | null>(null);

    const now = new Date();
    const timelineRange = resolveRequiredDateFilterRange(values, "timeline", "este_mes", now);
    const weekPeriods = comparisonWeekPeriods(now);

    const periodFilter = buildWidgetFilter({
        respondsTo: ["timeline", "clinic"],
        globalFilters: dailyAnalysisGlobalFilters,
        globalValues: values,
        now,
    });

    // O gráfico mostra dados até hoje + 41 dias (inclusive); o limite da query é exclusivo (+42 dias).
    const chartEndDateInclusive = addDays(now, 41);
    const chartEndExclusive = format(addDays(now, 42), "yyyy-MM-dd");
    const chartEndDateLabel = format(chartEndDateInclusive, "EEEE, d 'de' MMMM", { locale: pt });

    const currentWeekFilter = { start_date: { _gte: weekPeriods.current.gte, _lt: weekPeriods.current.lt } };

    const statusServiceResult = useAggregate(sessionsByStatusAndServiceQuery, { filter: periodFilter });
    const treatmentsFirstStartedSessionResult = useAggregate(treatmentsFirstStartedSessionQuery, { filter: EXCLUDE_VIRTUAL_CLINIC_FILTER });
    const treatmentsFirstSessionResult = useAggregate(treatmentsFirstStartedOrToComeSessionQuery, { filter: EXCLUDE_VIRTUAL_CLINIC_FILTER });
    const currentWeekResult = useAggregate(weeklySessionsByServiceQuery, { filter: currentWeekFilter });
    const previousWeekResult = useAggregate(weeklySessionsByServiceQuery, {
        filter: { start_date: { _gte: weekPeriods.previous.gte, _lt: weekPeriods.previous.lt } },
    });
    const chartLocalClauses = [
        { start_date: { _lt: chartEndExclusive } },
        EXCLUDE_VIRTUAL_CLINIC_FILTER,
        ...(chartClinic === null ? [] : [{ clinic_id: { _eq: chartClinic } }]),
        ...(chartClientType === null ? [] : [clientTypeFilterByOption[chartClientType]]),
    ];
    const monthlyResult = useAggregate(monthlySessionsQuery, { filter: { _and: chartLocalClauses } });

    const kpiValues = computeKpiCells(statusServiceResult.data || [], dailyAnalysisKpiCells);
    const upcomingTreatments = treatmentsWithFirstSessionInRange(treatmentsFirstSessionResult.data || [], timelineRange);
    const treatmentsStartedPlusToCome = upcomingTreatments.length;
    // "Iniciados" = o mesmo conjunto do "+ Por Vir" limitado aos tratamentos já iniciados, ou seja, cuja
    // primeira sessão NFB/Psi já arrancou — realizada, à espera na clínica ou em sessão — coincidindo com a
    // primeira sessão de qualquer estado. Deriva-se das duas aggregates globais, sem depender do enriquecimento do drill.
    const firstStartedDateByTreatmentId = new Map<number, string | null>(
        (treatmentsFirstStartedSessionResult.data || []).map((row: any) => [Number(row.treatment), row.min?.start_date ?? null])
    );
    const startedTreatments = upcomingTreatments.filter(
        (treatment) => firstStartedDateByTreatmentId.get(Number(treatment.treatmentId)) === treatment.firstSessionDate
    );
    const treatmentsStartedCount = startedTreatments.length;
    const chartPoints = monthlySeriesFromRows(monthlyResult.data || [], sessionsChartSeries);

    const upcomingTreatmentIds = upcomingTreatments.map((treatment) => treatment.treatmentId).slice(0, 800);
    const upcomingFirstSessionsResult = useFirstSessionsEnrichment(
        ["daily-upcoming-first-sessions", timelineRange.gte, timelineRange.lt, upcomingTreatmentIds.length, upcomingTreatmentIds[0]],
        upcomingTreatmentIds,
        TREATMENT_FIRST_SESSION_STARTED_OR_TO_COME_STATUSES,
        upcomingTreatmentIds.length > 0 && canDrill
    );
    const firstSessionByTreatmentId = firstSessionByTreatmentIdFromItems(upcomingFirstSessionsResult.data?.items || []);
    const serviceMixResult = useAggregate(treatmentsServiceMixQuery, {
        filter: { _and: [{ treatment: { _in: upcomingTreatmentIds } }, EXCLUDE_VIRTUAL_CLINIC_FILTER] },
        enabled: upcomingTreatmentIds.length > 0 && canDrill,
    });
    const servicesLabelByTreatmentId = treatmentServicesLabelByTreatmentId(serviceMixResult.data || [], NFB_SERVICE, PSY_SERVICE);
    const upcomingDrillRows = treatmentFirstSessionDrillRows(upcomingTreatments, firstSessionByTreatmentId, SESSION_STATUS_LABELS_PT, servicesLabelByTreatmentId);
    const startedDrillRows = treatmentFirstSessionDrillRows(startedTreatments, firstSessionByTreatmentId, SESSION_STATUS_LABELS_PT, servicesLabelByTreatmentId);
    const todayIso = format(now, "yyyy-MM-dd");
    // Chips do mix de serviços só quando o aggregate carregou (senão dariam "Só NFB: 0 / Só Psi: 0 / ...").
    const startedHeaderChips = [
        ...treatmentFirstSessionHeaderChips(startedDrillRows, todayIso, CLINIC_CHIP_NAMES),
        ...(serviceMixResult.data ? treatmentServiceMixChips(startedDrillRows) : []),
    ];
    const upcomingHeaderChips = [
        ...treatmentFirstSessionHeaderChips(upcomingDrillRows, todayIso, CLINIC_CHIP_NAMES),
        ...(serviceMixResult.data ? treatmentServiceMixChips(upcomingDrillRows) : []),
    ];
    // Chips por clínica nos cartões (só quando o enriquecimento carregou, senão dariam "Porto: 0/Braga: 0").
    const startedClinicChips = upcomingFirstSessionsResult.data ? treatmentClinicChips(startedDrillRows, CLINIC_CHIP_NAMES) : undefined;
    const upcomingClinicChips = upcomingFirstSessionsResult.data ? treatmentClinicChips(upcomingDrillRows, CLINIC_CHIP_NAMES) : undefined;

    const weekValues = {
        currentTotal: countWhere(currentWeekResult.data || [], { services: [NFB_SERVICE, PSY_SERVICE] }),
        currentNfb: countWhere(currentWeekResult.data || [], { services: [NFB_SERVICE] }),
        currentPsy: countWhere(currentWeekResult.data || [], { services: [PSY_SERVICE] }),
        previousTotal: countWhere(previousWeekResult.data || [], { services: [NFB_SERVICE, PSY_SERVICE] }),
        previousNfb: countWhere(previousWeekResult.data || [], { services: [NFB_SERVICE] }),
        previousPsy: countWhere(previousWeekResult.data || [], { services: [PSY_SERVICE] }),
    };

    const cellsByKey = Object.fromEntries(dailyAnalysisKpiCells.map((cell) => [cell.key, cell]));
    const isWeekLoading = currentWeekResult.isLoading || previousWeekResult.isLoading;

    const servicesResult = useRecords("services", "id,name");
    const serviceNames: Record<number, string> = Object.fromEntries(
        (servicesResult.data?.items || []).map((service: { id: number; name: string }) => [service.id, service.name])
    );
    const selectedClinicLabel = DAILY_ANALYSIS_CLINIC_OPTIONS.find((option) => option.value === values.clinic)?.label || null;
    const cellExplanationContext = { serviceNames, range: timelineRange, clinicLabel: selectedClinicLabel };

    const weekComparisonSuffix = ` Comparação com o mesmo cálculo na semana anterior completa (${formatDateRangePt(weekPeriods.previous)}). Semanas de segunda a domingo.`;
    const explanations = {
        evaluationsGauge: {
            queryDescription: describeSessionsCount({ ...evaluationsDoneGaugeMatch, serviceNames, range: timelineRange, clinicLabel: selectedClinicLabel }),
        },
        totalSessionsGauge: {
            queryDescription: describeSessionsCount({ ...totalSessionsDoneGaugeMatch, serviceNames, range: timelineRange, clinicLabel: selectedClinicLabel }),
            businessNote: "Os bónus mensais da equipa baseiam-se neste total.",
        },
        treatmentsStarted: {
            queryDescription: `Para cada tratamento considera a primeira sessão de Neurofeedback ou Psicologia já iniciada — com estado "realizada", "à espera na clínica" ou "em sessão" (avaliações e consultas não contam como início). Conta os tratamentos cuja primeira sessão nesse estado está ${formatDateRangePt(timelineRange)}. A coluna "Tratamento" e os chips "Só NFB / Só Psi / NFB + Psi" classificam cada tratamento pelos serviços de todas as suas sessões de Neurofeedback/Psicologia realizadas ou por vir.`,
        },
        treatmentsPlusToCome: {
            queryDescription: `Para cada tratamento considera a primeira sessão de Neurofeedback ou Psicologia com estado "marcada", "confirmada", "à espera na clínica", "em sessão" ou "realizada" (avaliações e consultas não contam como início). Conta os tratamentos cuja primeira dessas sessões está ${formatDateRangePt(timelineRange)}. A coluna "Tratamento" e os chips "Só NFB / Só Psi / NFB + Psi" classificam cada tratamento pelos serviços de todas as suas sessões de Neurofeedback/Psicologia realizadas ou por vir.`,
        },
        weekTotal: {
            queryDescription: describeSessionsCount({ statuses: DONE_AND_TO_COME_STATUSES, services: [NFB_SERVICE, PSY_SERVICE], serviceNames, range: weekPeriods.current }) + weekComparisonSuffix,
        },
        weekNfb: {
            queryDescription: describeSessionsCount({ statuses: DONE_AND_TO_COME_STATUSES, services: [NFB_SERVICE], serviceNames, range: weekPeriods.current }) + weekComparisonSuffix,
        },
        weekPsy: {
            queryDescription: describeSessionsCount({ statuses: DONE_AND_TO_COME_STATUSES, services: [PSY_SERVICE], serviceNames, range: weekPeriods.current }) + weekComparisonSuffix,
        },
        chart: {
            queryDescription: `${describeSessionsCount({ statuses: DONE_AND_TO_COME_STATUSES, services: [NFB_SERVICE, PSY_SERVICE], serviceNames, range: { gte: "2025-01-01", lt: chartEndExclusive } })} Os valores são agrupados por mês e vão até hoje + 41 dias (inclusive), ou seja, ${chartEndDateLabel}. Os filtros "Tipo de cliente" e "Clínica" deste cartão aplicam-se apenas ao gráfico, independentemente dos filtros globais. O "Tipo de cliente" baseia-se na idade no início do tratamento: "Maiores 18" = idade > 18; "Menores ou igual 18" = idade ≤ 18 ou sem idade registada.`,
        },
    };

    const sessionsDrillBase = {
        fieldsQuery: SESSIONS_DRILL_FIELDS,
        defaultSort: "start_date",
        columns: sessionsDrillColumns,
        rowRoute: sessionRoute,
    };

    const treatmentFirstSessionDrillBase = {
        kind: "rows" as const,
        columns: TREATMENT_FIRST_SESSION_COLUMNS,
        defaultSortKey: "firstSessionDate",
        defaultPageSize: 50,
        rowRoute: (row: any) => `/treatments/${row.id}`,
        rowSx: treatmentRowClinicSx,
    };

    const drills = {
        evaluationsGauge: drillDownFromAggregate(
            sessionsByStatusAndServiceQuery,
            combineFilters(periodFilter, cellMatchToFilter(evaluationsDoneGaugeMatch)),
            { title: "Avaliações Realizadas", queryDescription: explanations.evaluationsGauge.queryDescription, ...sessionsDrillBase }
        ),
        totalSessionsGauge: drillDownFromAggregate(
            sessionsByStatusAndServiceQuery,
            combineFilters(periodFilter, cellMatchToFilter(totalSessionsDoneGaugeMatch)),
            { title: "Total Sessões Realizadas", queryDescription: explanations.totalSessionsGauge.queryDescription, ...sessionsDrillBase }
        ),
        treatmentsStarted: {
            ...treatmentFirstSessionDrillBase,
            title: "Tratamentos Iniciados",
            queryDescription: explanations.treatmentsStarted.queryDescription,
            rows: startedDrillRows,
            headerChips: startedHeaderChips,
        },
        treatmentsPlusToCome: {
            ...treatmentFirstSessionDrillBase,
            title: "Tratamentos Iniciados + Por Vir",
            queryDescription: explanations.treatmentsPlusToCome.queryDescription,
            rows: upcomingDrillRows,
            headerChips: upcomingHeaderChips,
        },
        weekTotal: drillDownFromAggregate(weeklySessionsByServiceQuery, currentWeekFilter, {
            title: "Esta semana",
            queryDescription: explanations.weekTotal.queryDescription,
            ...sessionsDrillBase,
        }),
        weekNfb: drillDownFromAggregate(weeklySessionsByServiceQuery, combineFilters(currentWeekFilter, { service: { _in: [NFB_SERVICE] } }), {
            title: "Esta semana - NFB",
            queryDescription: explanations.weekNfb.queryDescription,
            ...sessionsDrillBase,
        }),
        weekPsy: drillDownFromAggregate(weeklySessionsByServiceQuery, combineFilters(currentWeekFilter, { service: { _in: [PSY_SERVICE] } }), {
            title: "Esta semana - Psicologia",
            queryDescription: explanations.weekPsy.queryDescription,
            ...sessionsDrillBase,
        }),
    };

    const renderKpiCard = (cellKey: string, color: string, span?: number) => {
        const cellExplanation = describeKpiCell(cellsByKey[cellKey], dailyAnalysisKpiCells, cellExplanationContext);
        const card = (
            <StatCard
                key={cellKey}
                label={cellsByKey[cellKey].label}
                value={statusServiceResult.data ? kpiValues[cellKey] : undefined}
                color={color}
                explanation={cellExplanation}
                isLoading={statusServiceResult.isLoading}
                drillDown={drillDownForKpiCell(sessionsByStatusAndServiceQuery, periodFilter, dailyAnalysisKpiCells, cellKey, {
                    title: cellsByKey[cellKey].label,
                    queryDescription: cellExplanation.queryDescription,
                    ...sessionsDrillBase,
                })}
                sx={span ? { gridColumn: `span ${span}` } : undefined}
            />
        );
        return card;
    };

    return (
        <DashboardPage globalFilters={dailyAnalysisGlobalFilters} values={values} onChange={setValue}>
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6} lg={3}>
                    <GaugeCard
                        title="Avaliações Realizadas"
                        value={statusServiceResult.data ? countWhere(statusServiceResult.data, evaluationsDoneGaugeMatch) : undefined}
                        goal={dailyAnalysisGaugeGoals.evaluationsDone}
                        explanation={explanations.evaluationsGauge}
                        isLoading={statusServiceResult.isLoading}
                        isError={statusServiceResult.isError}
                        drillDown={drills.evaluationsGauge}
                    />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    <Box display="flex" flexDirection="column" gap={3} height="100%">
                        <StatCard
                            label="Tratamentos Iniciados"
                            value={treatmentsFirstStartedSessionResult.data && treatmentsFirstSessionResult.data ? treatmentsStartedCount : undefined}
                            color={DASHBOARD_COLORS.highlight}
                            explanation={explanations.treatmentsStarted}
                            isLoading={treatmentsFirstStartedSessionResult.isLoading || treatmentsFirstSessionResult.isLoading}
                            drillDown={drills.treatmentsStarted}
                            chips={startedClinicChips}
                        />
                        <StatCard
                            label="Tratamentos Iniciados + Por Vir"
                            value={treatmentsFirstSessionResult.data ? treatmentsStartedPlusToCome : undefined}
                            color={DASHBOARD_COLORS.highlight}
                            explanation={explanations.treatmentsPlusToCome}
                            isLoading={treatmentsFirstSessionResult.isLoading}
                            drillDown={drills.treatmentsPlusToCome}
                            chips={upcomingClinicChips}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    <GaugeCard
                        title="Total Sessões Realizadas"
                        value={statusServiceResult.data ? countWhere(statusServiceResult.data, totalSessionsDoneGaugeMatch) : undefined}
                        goal={dailyAnalysisGaugeGoals.totalSessionsDone}
                        explanation={explanations.totalSessionsGauge}
                        isLoading={statusServiceResult.isLoading}
                        isError={statusServiceResult.isError}
                        drillDown={drills.totalSessionsGauge}
                    />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    <Box display="flex" flexDirection="column" gap={3} height="100%">
                        <ComparisonCard
                            label="Esta semana"
                            value={weekValues.currentTotal}
                            previousValue={weekValues.previousTotal}
                            previousLabel="Semana passada"
                            explanation={explanations.weekTotal}
                            isLoading={isWeekLoading}
                            drillDown={drills.weekTotal}
                        />
                        <ComparisonCard
                            label="Esta semana - NFB"
                            value={weekValues.currentNfb}
                            previousValue={weekValues.previousNfb}
                            previousLabel="Semana passada"
                            explanation={explanations.weekNfb}
                            isLoading={isWeekLoading}
                            drillDown={drills.weekNfb}
                        />
                        <ComparisonCard
                            label="Esta semana - Psicologia"
                            value={weekValues.currentPsy}
                            previousValue={weekValues.previousPsy}
                            previousLabel="Semana passada"
                            explanation={explanations.weekPsy}
                            isLoading={isWeekLoading}
                            drillDown={drills.weekPsy}
                        />
                    </Box>
                </Grid>
            </Grid>

            {dailyAnalysisKpiRows.map((kpiRow) => {
                const placements = kpiRow.cells || (kpiRow.cellKeys || []).map((cellKey) => ({ cellKey, color: kpiRow.color, span: undefined }));
                return (
                    <Box
                        key={kpiRow.key}
                        display="grid"
                        gridTemplateColumns={kpiRow.gridTemplateColumns || "repeat(auto-fit, minmax(160px, 1fr))"}
                        gap={3}
                    >
                        {placements.map((placement) => renderKpiCard(placement.cellKey, placement.color, placement.span))}
                    </Box>
                );
            })}

            <WidgetCard
                title={`Sessões Realizadas Psi + NFB — dados até ${chartEndDateLabel}`}
                explanation={explanations.chart}
                bodyHeight={380}
                isLoading={monthlyResult.isLoading}
                isError={monthlyResult.isError}
                headerControls={
                    <>
                        <TextField
                            select
                            size="small"
                            label="Tipo de cliente"
                            value={chartClientType === null ? ALL_OPTION_VALUE : chartClientType}
                            onChange={(event) => {
                                const newValue = event.target.value;
                                setChartClientType(newValue === ALL_OPTION_VALUE ? null : newValue);
                            }}
                            sx={{ minWidth: 180 }}
                        >
                            <MenuItem value={ALL_OPTION_VALUE}>Todos</MenuItem>
                            {CLIENT_TYPE_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            size="small"
                            label="Clínica"
                            value={chartClinic === null ? ALL_OPTION_VALUE : chartClinic}
                            onChange={(event) => {
                                const newValue = event.target.value;
                                setChartClinic(newValue === ALL_OPTION_VALUE ? null : Number(newValue));
                            }}
                            sx={{ minWidth: 160 }}
                        >
                            <MenuItem value={ALL_OPTION_VALUE}>Todas</MenuItem>
                            {DAILY_ANALYSIS_CLINIC_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <Button
                            variant="text"
                            size="small"
                            onClick={() => {
                                setChartClinic(null);
                                setChartClientType(null);
                            }}
                        >
                            Repor
                        </Button>
                    </>
                }
            >
                <TimeSeriesChart points={chartPoints} series={sessionsChartSeries} />
            </WidgetCard>
        </DashboardPage>
    );
};
